import { SUPPORT_EMAIL } from "./links";

export interface LegalSection {
  id: string;
  heading: string;
  paragraphs?: string[];
  list?: string[];
  table?: { headers: string[]; rows: string[][] };
  links?: { label: string; href: string }[];
}
export interface LegalDoc {
  title: string;
  category: string;
  updated: string;
  intro: string;
  summary: string[];
  sections: LegalSection[];
}

// Keep draft status until operator details and the operational review in
// LEGAL_REVIEW.md have been completed. A brand name is not a controller identity.
export const LEGAL_STATUS = "Draft for review";
const UPDATED = "October 1, 2026";
const contact = `Contact ${SUPPORT_EMAIL}. Do not send passwords, verification codes, private encryption keys or your backup PIN.`;
const operator: LegalSection = {
  id: "operator",
  heading: "Who provides Allading",
  paragraphs: [
    "Allading is the product name used in these documents. The operator’s full legal name, registered address and country of establishment must be supplied before these documents are adopted. ‘We’, ‘us’ and ‘our’ mean that operator; in the Privacy Policy, it is the controller of the personal information described here.",
    contact,
  ],
};

export const privacy: LegalDoc = {
  title: "Privacy Policy",
  category: "Your information",
  updated: UPDATED,
  intro:
    "Understand what Allading processes, how chat modes differ, and how to exercise your privacy choices. This notice covers the app, website and related messaging services.",
  summary: [
    "End-to-end encryption applies to supported secure chats, not ordinary or group chats.",
    "Account, device and delivery information is needed to operate the service.",
    "You can request access, correction or deletion without sharing your backup PIN.",
  ],
  sections: [
    operator,
    {
      id: "data",
      heading: "Information we process",
      table: {
        headers: ["Category", "Information and source", "Purpose"],
        rows: [
          [
            "Account & profile",
            "Email, password hash, verification status, user ID, handle, display name and optional avatar supplied by you. Phone details where a supported registration flow uses them.",
            "Registration, authentication, account recovery and showing your chosen profile.",
          ],
          [
            "Social connections",
            "Contact requests, contacts, chat membership, reactions, read acknowledgements and presence timestamps. Other users can provide information about you through these interactions.",
            "Connecting participants, chat administration and delivery status.",
          ],
          [
            "Messages & media",
            "Secure-chat ciphertext; ordinary-chat content processed by the server; uploaded photos, voice notes and related metadata.",
            "Delivering messages and maintaining the history for the selected chat mode.",
          ],
          [
            "Devices & security",
            "Device identifiers, platform, push tokens, public identity keys and prekeys, session records, integrity checks and network/security logs such as IP addresses and request timestamps.",
            "Sign-in security, routing notifications, encryption setup and abuse prevention.",
          ],
          [
            "Calls",
            "Room and participant identifiers, call type, signaling and connection events, timestamps, outcome and duration; media transmitted for the call.",
            "Connecting calls, troubleshooting and creating call summaries.",
          ],
          [
            "Optional backup & support",
            "PIN-protected encrypted key backup if enabled; correspondence and diagnostic information you choose to send.",
            "Restoring keys and responding to support or rights requests.",
          ],
        ],
      },
      paragraphs: [
        "Required account information enables registration and authentication; without it we cannot provide an account. An avatar, key backup and camera access are optional. Declining a permission may prevent the corresponding feature from working.",
      ],
    },
    {
      id: "chat-modes",
      heading: "Secure chats, cloud history and media",
      paragraphs: [
        "Supported secure chats use Signal Protocol end-to-end encryption. Devices encrypt message content before sending it; the service relays ciphertext. This does not hide all metadata, secure a compromised device or prevent a recipient from copying content. Availability depends on platform and app version; check the chat’s actual mode rather than assuming every conversation is secure.",
        "Ordinary chats, including group chats, use server-managed encryption. The server processes plaintext to encrypt and deliver cloud history and can decrypt this history. These conversations are not end-to-end encrypted. Authorized processing of their content may be necessary for delivery, support or legal obligations.",
        "Attachments follow the protection provided by their supported chat feature. Profile information, presence, call summaries and notification data are not covered by a blanket promise that all app data is end-to-end encrypted. Key restoration is separate from restoring message history.",
        "Recipients control their devices and may save, forward, screenshot or record content. Deletion cannot recall a copy already received, downloaded, exported or included in someone else’s backup.",
      ],
    },
    {
      id: "calls",
      heading: "Calls, screen sharing and network exposure",
      paragraphs: [
        "Voice and video calls use WebRTC transport encryption. The current service has no server-side call-recording feature. Other participants may still record a call; you are responsible for obtaining any consent required before recording or sharing someone’s voice, image or screen.",
        "Call setup exchanges network connection information. Depending on the connection route, another participant may learn your public IP address; a TURN relay provider also processes connection information. Transport encryption does not make calls anonymous.",
        "Screen sharing sends the content you select after the system capture prompt. Close sensitive windows and notifications before sharing. Microphone, camera and screen access are controlled by system permissions.",
      ],
    },
    {
      id: "purposes",
      heading: "Purposes and legal bases",
      paragraphs: [
        "Where GDPR or similar rules apply, the proposed bases below must be confirmed by the operator against its actual activities. Accepting the User Agreement is not blanket consent to every use of personal information.",
      ],
      table: {
        headers: ["Activity", "Proposed legal basis"],
        rows: [
          [
            "Account creation, authentication, messaging, calls and requested backup",
            "Performance of the service contract (GDPR Article 6(1)(b)).",
          ],
          [
            "Fraud prevention, network protection, proportionate diagnostics and support",
            "Legitimate interests in operating a safe, reliable service (Article 6(1)(f)), subject to necessity and a balancing assessment.",
          ],
          [
            "Responding to binding legal demands and mandatory recordkeeping",
            "Compliance with a specific applicable legal obligation (Article 6(1)(c)).",
          ],
          [
            "Non-essential cookies or optional processing requiring consent",
            "Consent (Article 6(1)(a)), requested separately where required and withdrawable without affecting earlier lawful processing.",
          ],
        ],
      },
    },
    {
      id: "sharing",
      heading: "Who receives information",
      list: [
        "People you communicate with receive the profile, messages, media, presence and call information relevant to the interaction. Group participants and administrators see information associated with their group roles.",
        "Cloudflare supplies the Worker backend, database, object storage and call relay infrastructure. Google Firebase supplies app integrity and push messaging; Apple supplies iOS push and system-call infrastructure. Resend supplies verification email delivery and processes the recipient address and email content. The operator must confirm the complete provider list and contractual roles before publication.",
        "Support personnel and service providers may receive information necessary for their assigned tasks. Providers may also process certain information under their own terms, for example for their platform security.",
        "Information may be disclosed to competent authorities where a valid law requires it, or where legally permitted and necessary to protect users, investigate abuse or establish or defend legal claims. We cannot supply plaintext we do not possess.",
        "A business transfer may involve service data, subject to applicable law, appropriate safeguards and notice of a change of controller.",
      ],
      paragraphs: [
        "The website currently contains no advertising or behavioral analytics integration. A future advertising, sale or targeted-sharing arrangement would require a revised notice and any legally required choices before it starts. This statement does not describe recipients’ independent actions.",
      ],
    },
    {
      id: "transfers",
      heading: "International processing",
      paragraphs: [
        "Infrastructure and recipients may process data outside your home country, including where global hosting and push networks operate. The operator must identify the relevant destinations and confirm the applicable transfer mechanism before offering the service in a jurisdiction that requires it.",
        "For restricted EEA or UK transfers, an applicable adequacy decision or an appropriate contractual safeguard, such as the EU Standard Contractual Clauses and a UK mechanism where needed, must cover the transfer. This draft does not assert that these arrangements have already been executed. You can request information about the safeguards and a copy, subject to lawful redactions, using the contact below.",
      ],
    },
    {
      id: "retention",
      heading: "How long information stays",
      table: {
        headers: ["Information", "Retention approach"],
        rows: [
          [
            "Active account and profile",
            "While needed to provide the account; account deletion removes or replaces identifying profile fields and revokes access.",
          ],
          [
            "Secure-chat server copies",
            "Time-limited delivery storage. The current standard client requests a 24-hour lifetime; expiration and cleanup do not erase recipients’ local copies.",
          ],
          [
            "Ordinary cloud history & call summaries",
            "Retained as cloud history, rather than governed by the secure-message expiration timer. Account deletion alone does not purge all conversation records.",
          ],
          [
            "Key backups, keys & push tokens",
            "The current account deletion flow removes server key backups and prekeys, clears device push tokens and revokes sessions.",
          ],
          [
            "Security, support, backups & residual records",
            "Only for a documented operational purpose, a legal retention duty or a necessary legal claim. Exact schedules, backup rotation and deletion completion times require operator confirmation.",
          ],
        ],
      },
      paragraphs: [
        "Retention decisions must consider the purpose, sensitivity, account status and relevant legal requirements. Information retained under an exception must be limited to that purpose. Scheduled cleanup, replicas or backups can make removal asynchronous; we do not promise instant deletion from every system.",
      ],
    },
    {
      id: "deletion",
      heading: "Account and message deletion",
      paragraphs: [
        "Use Delete account in the app’s safety controls, or request deletion using the account-deletion page. Uninstalling the app or signing out does not delete the account.",
        "The current deletion implementation deactivates and anonymizes the profile, revokes sessions and clears certain keys and device data. Some identifiers, relationship records, cloud messages and call history remain. Where you request further erasure, the operator must assess and address those records rather than describe this flow as deletion of every item.",
        "Deleting your own message leaves a deletion marker. Backend attachment cleanup is separate from updating the message record; copies already held by recipients remain outside our control.",
      ],
      links: [{ label: "Request account deletion", href: "/delete-account" }],
    },
    {
      id: "rights",
      heading: "Your rights and choices",
      list: [
        "Depending on the applicable law and processing basis, request access, correction, deletion, restriction and a portable copy of information you provided.",
        "Object to processing based on legitimate interests on grounds relating to your situation. Where direct marketing is used, you can object to it at any time.",
        "Withdraw consent for consent-based processing. You can also revoke device permissions in system settings; disabling a permission can limit a feature.",
        "For requests governed by GDPR, the normal response deadline is one month; a lawful extension of up to two further months may apply for complexity or multiple requests, with notice in the first month. Other jurisdictions may have different deadlines.",
        "We may request proportionate proof of account control to protect you. Requests are normally free; any lawful refusal or fee must be explained. An authorized agent may act where local law permits, subject to verification.",
      ],
      paragraphs: [
        contact,
        "You may complain to the data protection authority where you live, work or believe an infringement occurred. UK users may contact the ICO; Ukrainian users may contact the Ukrainian Parliament Commissioner for Human Rights. Contacting us first is optional, not a condition of complaining.",
      ],
      links: [
        {
          label: "UK Information Commissioner",
          href: "https://ico.org.uk/make-a-complaint/",
        },
        {
          label: "Ukrainian Parliament Commissioner for Human Rights",
          href: "https://ombudsman.gov.ua/",
        },
      ],
    },
    {
      id: "security",
      heading: "Security and automated protection",
      paragraphs: [
        "The service uses authentication, access controls and encryption appropriate to the feature. No service can guarantee absolute security. Keep your devices updated and protect your credentials and backup PIN; report suspected compromise without including secrets.",
        "Rate limits and integrity checks can automatically reject requests or restrict access. Contact support if you believe a restriction is wrong. The operator must assess whether any deployed automated process has legal or similarly significant effects and provide the disclosures and safeguards required by applicable law.",
        "If a personal-data breach requires notification under applicable law, the operator must notify the relevant authority and affected individuals within the required time and conditions.",
      ],
    },
    {
      id: "children",
      heading: "Children and age requirements",
      paragraphs: [
        "Allading is not designed for children under 13. A higher local minimum age may apply. Where parental authorization is legally required, the service must not process a child’s information on that basis without a valid authorization process. The operator must confirm supported ages and countries before launch; this draft does not establish an age-verification system.",
        "If you believe a child is using the service contrary to these requirements, contact support with enough information to locate the account. Do not send unnecessary sensitive information about the child.",
      ],
    },
    {
      id: "website",
      heading: "Website technologies",
      paragraphs: [
        "This website serves static resources and currently uses no optional analytics or advertising cookies in its application code. Hosting systems can still process access and security information. Fonts use your device’s installed typefaces to avoid a separate font-service request. Store links take you to third-party services with their own privacy terms.",
      ],
      links: [{ label: "Cookies & website notice", href: "/cookies" }],
    },
    {
      id: "changes",
      heading: "Changes and contact",
      paragraphs: [
        "The date above identifies this draft version, not proof of acceptance or a retroactive effective date. Once adopted, material changes must be communicated appropriately before taking effect, unless an urgent legal or security requirement needs a shorter timeline. New consent will be requested where required.",
        contact,
      ],
    },
  ],
};

export const terms: LegalDoc = {
  title: "User Agreement",
  category: "Using Allading",
  updated: UPDATED,
  intro:
    "The rules for accounts, conversations and calls on Allading. These terms explain your responsibilities, the service’s limits and the rights that remain yours.",
  summary: [
    "You keep ownership of your content; service permissions are limited to operating Allading.",
    "Calls need connectivity and do not connect to emergency services.",
    "Mandatory consumer and privacy rights remain protected.",
  ],
  sections: [
    operator,
    {
      id: "scope",
      heading: "Scope and acceptance",
      paragraphs: [
        "This agreement covers the Allading app, messaging and calling services and website. ‘You’ means the person using them. Before account registration, you must receive access to the applicable agreement and any notices required by law. Where affirmative acceptance is required, merely visiting the website does not replace it.",
        "The Privacy Policy explains processing of personal information; it is a notice, not a blanket consent or waiver of your privacy rights. Additional feature or purchase terms must be presented before that feature or purchase is accepted. Mandatory law takes priority over conflicting wording here.",
      ],
      links: [{ label: "Read the Privacy Policy", href: "/privacy" }],
    },
    {
      id: "eligibility",
      heading: "Eligibility and your account",
      list: [
        "You must be at least 13 and meet any higher local age or legal-capacity requirement. Where required, a parent or guardian must validly authorize use. Supported ages and countries remain subject to operator confirmation.",
        "Register with details you are entitled to use; do not impersonate another person. Protect your email account, password, verification codes, devices and backup PIN.",
        "Tell support promptly if you suspect unauthorized access. Responsibility for account activity is subject to applicable law and does not make you automatically liable for conduct outside your control.",
        "If using Allading for an organization, you must have authority to bind it. Organization use does not displace an individual’s mandatory rights.",
      ],
    },
    {
      id: "license",
      heading: "License and intellectual property",
      paragraphs: [
        "Subject to this agreement, the operator grants you a limited, non-exclusive license to install and use the app on devices you own or control, consistent with the relevant store usage rules. The service name, branding and proprietary software remain with their respective rights holders.",
        "You may not misuse the service, bypass access controls or copy or redistribute proprietary material without permission. Nothing prohibits reverse engineering, interoperability, security research or other activity protected by mandatory law. Open-source components remain governed by their own licenses; this agreement does not remove those rights.",
      ],
    },
    {
      id: "content",
      heading: "Your content and service permissions",
      paragraphs: [
        "You retain rights in content you create. You must have the rights and permissions needed to share it, including permissions concerning other people’s personal information, image and voice.",
        "You give the operator a non-exclusive, worldwide, royalty-free permission, limited to operating the service, to host, process, transmit, display to intended recipients and technically adapt content for delivery. Infrastructure providers may exercise those permissions only as needed for that purpose. This is not a transfer of ownership or permission to use private messages in advertising.",
        "The permission lasts while content is held for service delivery or a lawful retention purpose. Removing content ends future service use except for necessary cleanup, lawful retention and copies already delivered to recipients. Secure-chat encryption limits the content the operator can access.",
      ],
    },
    {
      id: "acceptable-use",
      heading: "Acceptable use",
      list: [
        "Exploit or sexually abuse children, distribute child sexual abuse material, groom children or facilitate trafficking.",
        "Threaten violence, stalk, harass, dox or engage in unlawful discrimination; promote or facilitate terrorism or other unlawful conduct.",
        "Send fraud, phishing, spam, deceptive bulk messages or malicious impersonation, or obtain credentials through deception.",
        "Distribute malware, compromise accounts, evade lawful restrictions, overload systems or access information without authorization.",
        "Infringe copyright, trademarks, privacy or other rights, or disclose confidential information without authority.",
        "Collect other users’ data, scrape accounts or automate unwanted interactions without permission or a lawful basis.",
        "Create accounts to evade an enforcement decision, or use bots and integrations to carry out prohibited activity.",
      ],
      paragraphs: [
        "These rules apply to profile details, chat requests, messages, calls, files, groups, bots and integrations. Local laws may impose additional obligations. You must not use Allading to:",
      ],
    },
    {
      id: "reports",
      heading: "Reporting abuse and rights violations",
      paragraphs: [
        contact,
        "Describe the account or content concerned, the relevant user/chat/message identifier if available, the reason for your report, applicable rights and enough context to assess it. Provide only evidence you are entitled to share. For an intellectual-property complaint, identify the protected work, your authority and the specific disputed material; include a good-faith statement that the report is accurate.",
        "End-to-end encryption limits what the operator can inspect. You may voluntarily supply relevant evidence, but do not expose unrelated private conversations. Reporting does not guarantee removal. Do not make knowingly false or abusive reports.",
        "The operator may restrict access, remove material it controls or preserve evidence when justified and lawful. Where required or reasonably practicable, it will explain an enforcement decision and allow review. You can challenge an account restriction by contacting support; this does not limit a statutory appeal or court remedy.",
      ],
    },
    {
      id: "privacy",
      heading: "Chat modes, backups and deletion",
      paragraphs: [
        "Secure chat is a supported feature, not a guarantee that all Allading activity is end-to-end encrypted. Ordinary and group chats use server-managed cloud history. Confirm the conversation mode before sharing sensitive material.",
        "Recipients may retain your content. Editing or deleting a message cannot remove a copy they already saved. A key backup does not back up every message or attachment; losing your PIN or device keys can prevent recovery.",
        "Account deletion revokes access and anonymizes the profile but does not automatically purge all shared cloud history, call summaries or recipient copies. Use the deletion notice to understand the current flow and request further erasure where applicable.",
      ],
      links: [{ label: "Account deletion details", href: "/delete-account" }],
    },
    {
      id: "calls",
      heading: "Calls, recording and emergencies",
      paragraphs: [
        "Allading does not provide emergency calling, a public telephone number or a replacement for an emergency service. Use your local emergency number through an appropriate telephone service. Do not rely on Allading for safety-critical communication.",
        "Call availability depends on internet access, device compatibility, permissions, push delivery and system settings. A notification or queued message does not establish that the recipient received or answered it.",
        "Obtain legally required consent before recording calls or sharing another person’s voice, image or screen. Screen sharing can reveal sensitive information; review what is visible before enabling it. Allading’s current service does not offer server-side call recording.",
      ],
    },
    {
      id: "mesh",
      heading: "Connectivity and experimental features",
      paragraphs: [
        "Cloud connectivity is the primary messaging and calling transport. Bluetooth and Wi-Fi mesh tools on supported Android devices are diagnostic or experimental features; they do not guarantee offline delivery, anonymity or reliable emergency communication.",
        "Experimental features can have limits or change. Use supported permissions and comply with local rules for wireless communication. Pending messages may expire, fail or need manual attention. Carrier, data and roaming charges are your responsibility.",
      ],
    },
    {
      id: "third-parties",
      heading: "Third-party services, bots and links",
      paragraphs: [
        "App stores, operating systems, email, push and network providers have their own terms. Links are provided for convenience and do not mean the operator endorses every third-party practice.",
        "If you interact with a bot, webhook or integration, the relevant third party may receive messages or other data you intentionally share with it. Verify its identity and privacy terms; do not assume an integration is operated by Allading or has secure-chat protections. Permissions must be limited to what you authorize.",
      ],
    },
    {
      id: "payments",
      heading: "Charges and future paid features",
      paragraphs: [
        "These terms do not by themselves authorize a charge or recurring subscription. Any paid feature must disclose price, currency, taxes, billing interval, renewal, cancellation and applicable refunds before purchase. Applicable store purchase terms and mandatory withdrawal or refund rights remain in effect.",
        "Stopping use or deleting an account may not cancel a separately purchased store subscription; if one is offered, cancel it through the relevant billing provider. No clause here creates a paid plan that is not offered.",
      ],
    },
    {
      id: "availability",
      heading: "Availability, changes and updates",
      paragraphs: [
        "The operator will exercise the care required by applicable law. Service availability and compatibility can vary, and no uninterrupted operation, perfect security or permanent cloud archive is promised. Install relevant security updates and keep appropriate copies of content you need.",
        "Features may be changed or discontinued for legitimate operational, security or legal reasons. Where law requires notice, continued conformity, remedies or refunds, those obligations apply. Material changes should be notified with a reasonable opportunity to stop use or exercise applicable rights.",
        "Events outside reasonable control, such as network failures or natural disasters, may interrupt service. This does not excuse duties or remedies that cannot legally be excluded.",
      ],
    },
    {
      id: "termination",
      heading: "Suspension and termination",
      paragraphs: [
        "You may stop use and request account deletion. The operator may restrict or terminate access for a material breach, a binding legal obligation or a substantiated risk to people or the service. Enforcement should be proportionate; urgent protection may require immediate action.",
        "Where lawful and practicable, the operator should explain the reason and provide an opportunity to correct a breach or request review. Export or save accessible information before closure when possible. Suspension does not remove privacy rights or justify indefinite retention.",
        "Clauses concerning existing rights, lawful retention, disputes and liabilities accrued before termination continue only as necessary for those purposes.",
      ],
    },
    {
      id: "liability",
      heading: "Warranties and liability",
      paragraphs: [
        "Subject to mandatory law, the service is provided on an ‘as available’ basis and implied warranties are excluded only to the extent their exclusion is permitted. For consumers, statutory quality, conformity and remedy rights remain intact.",
        "To the extent permitted by applicable law, the operator is not responsible for unforeseeable losses or indirect or consequential losses arising from use. This clause does not impose a monetary cap or require users to indemnify the operator for every third-party claim.",
        "Nothing excludes or limits liability for fraud, deliberate wrongdoing, death or personal injury caused by negligence, or any other liability that applicable law forbids excluding or limiting. Your privacy, consumer and other mandatory rights cannot be waived by these terms.",
      ],
    },
    {
      id: "apple",
      heading: "Apple and Google distribution",
      paragraphs: [
        "For an app downloaded through Apple, this agreement is between you and the operator, not Apple. Usage must comply with Apple’s applicable rules, including permitted family sharing or volume-purchase use. The operator, not Apple, is responsible for app support and maintenance, product claims and applicable legal compliance.",
        "If the Apple app fails to conform to an applicable warranty, you may notify Apple; any refund of the purchase price is governed by applicable store rules. To the maximum extent permitted by law, Apple has no other warranty obligation. The operator is responsible for addressing claims relating to the app, including product liability, legal or consumer compliance and third-party intellectual-property claims.",
        "Apple and its subsidiaries are third-party beneficiaries of the applicable Apple app-license provisions and may enforce them after your acceptance. You must comply with applicable third-party agreements and export restrictions, including relevant US embargo and restricted-party rules where they lawfully apply.",
        "For Google Play distribution, applicable Google Play rules also apply. Store involvement does not remove the operator’s obligations or your mandatory remedies. The operator’s legal contact details must be completed before this draft is used as a custom app-store license.",
      ],
    },
    {
      id: "disputes",
      heading: "Applicable law and disputes",
      paragraphs: [
        "The operator’s country and a governing-law provision must be confirmed before adoption. This draft selects no invented jurisdiction, exclusive foreign court, compulsory arbitration or class-action waiver.",
        "Mandatory protections and access to competent courts available under the law of your habitual residence remain unaffected. You can contact support to seek an informal resolution; doing so is not a prerequisite to a statutory complaint or court action.",
      ],
    },
    {
      id: "general",
      heading: "General provisions and changes",
      paragraphs: [
        "If a clause is unenforceable, the remaining agreement applies to the extent lawful; an invalid clause must not be replaced in a way that reduces mandatory rights. A delay in enforcing a right does not waive it. Transfer of the service or agreement must respect applicable law and must not reduce protected consumer rights.",
        "The operator must announce material revisions and their effective date through appropriate channels, such as the app, email or website, with advance notice where required. An update does not retroactively remove accrued rights. Obtain fresh acceptance or consent when required; silence alone is not treated as consent to new personal-data uses.",
        "The date on this page is the draft revision date. The final operator must approve a version and effective date before adoption. If translations are provided, no language-priority provision overrides mandatory local disclosure rules.",
      ],
    },
    {
      id: "contact",
      heading: "Contact and notices",
      paragraphs: [
        contact,
        "Use a subject that identifies your request, such as ‘Privacy request’, ‘Account deletion’, ‘Abuse report’ or ‘Account restriction review’. Provide a reply address and only the information needed to identify the issue. Operator identity, postal address and any required representative contacts remain to be completed.",
      ],
    },
  ],
};

export const cookies: LegalDoc = {
  title: "Cookies & Website Notice",
  category: "On this website",
  updated: UPDATED,
  intro:
    "A focused explanation of website requests, external links and browser storage. This notice covers the website; app processing is described in the Privacy Policy.",
  summary: [
    "No optional advertising or analytics cookies are configured in the current website code.",
    "Website hosting still processes network requests and security information.",
    "External store links have their own terms and privacy practices.",
  ],
  sections: [
    {
      id: "storage",
      heading: "Cookies and browser storage",
      paragraphs: [
        "The current website application does not set optional analytics, advertising or preference cookies and does not use local storage for visitor profiling. Infrastructure may use strictly necessary security technologies; the operator must verify the deployed hosting behavior before publication.",
        "A consent banner is not a substitute for an accurate inventory. If non-essential trackers are added, the operator must disclose their names, providers, purposes and lifetimes and obtain prior consent where required. Rejecting and withdrawing consent must be as accessible as accepting it.",
      ],
    },
    {
      id: "requests",
      heading: "Hosting and resource requests",
      paragraphs: [
        "Your browser sends network information, such as an IP address, requested resource, timestamp and browser headers, to the website host to load and protect the site. The operator must confirm hosting-log purposes, legal bases, recipients and retention.",
        "App previews and icons are bundled with the website; fonts use your device’s installed typefaces. Opening a Google Play or Apple store link creates a separate request to that provider. Sending an email uses your email provider and the recipient’s mail infrastructure.",
      ],
    },
    {
      id: "controls",
      heading: "Your browser controls",
      paragraphs: [
        "You can review or clear stored site data and adjust cookie controls in your browser. Blocking a strictly necessary technology may affect site functionality. Browser controls do not delete an Allading app account or remove its cloud history.",
      ],
    },
    {
      id: "contact",
      heading: "Questions and related information",
      paragraphs: [contact],
      links: [
        { label: "Privacy Policy", href: "/privacy" },
        { label: "Request account deletion", href: "/delete-account" },
      ],
    },
  ],
};

export const deletion: LegalDoc = {
  title: "Delete Your Allading Account",
  category: "Account control",
  updated: UPDATED,
  intro:
    "Request deletion through the app or by email. Understand what the current account-deletion flow removes and what needs a separate erasure assessment.",
  summary: [
    "Uninstalling the app does not delete your account.",
    "The current flow anonymizes your profile and revokes access; some shared records remain.",
    "Never send your password, verification code or backup PIN.",
  ],
  sections: [
    {
      id: "request",
      heading: "How to request deletion",
      list: [
        "In the app: open the safety controls in Settings, choose Delete account and confirm. You need an internet connection to complete the server request.",
        `Without the app: email ${SUPPORT_EMAIL} with the subject ‘Allading account deletion’. Prefer the email address registered to your account; otherwise include your handle or user ID so the account can be located.`,
        "The operator may ask for proportionate proof of account control. Do not send credentials or identity documents unless a necessary, secure verification process has been explained.",
      ],
      links: [
        {
          label: "Email an account-deletion request",
          href: `mailto:${SUPPORT_EMAIL}?subject=Allading%20account%20deletion`,
        },
      ],
    },
    {
      id: "removed",
      heading: "What the current flow removes or disables",
      list: [
        "Account sign-in is disabled and the profile is replaced with a deleted-account marker; identifying email, phone, handle and avatar fields are removed or replaced.",
        "Sessions are revoked, push tokens and device public-key fields are cleared, and server key backups and prekey bundles are removed.",
        "Contact requests and active-call participation records are removed. Owned bots are marked deleted and their tokens revoked.",
      ],
    },
    {
      id: "remaining",
      heading: "What can remain",
      paragraphs: [
        "The current implementation does not automatically purge all cloud messages, chat membership or contact relationship records, call summaries, session/device records or every identifier associated with the account. This is not a promise that all of those records may lawfully be retained indefinitely.",
        "Ask for further erasure of associated personal information if that is your intention. The operator must assess remaining records, explain any lawful exception and delete or minimize information that no longer has a valid purpose. Recipient copies and independent backups may remain outside the operator’s control.",
        "A separate message-deletion action leaves a tombstone in conversation history and triggers backend attachment cleanup. Account deletion cannot recall previously saved screenshots, recordings or downloaded files.",
      ],
    },
    {
      id: "timing",
      heading: "Timing, exceptions and confirmation",
      paragraphs: [
        "The server account action takes effect when the request succeeds. A manually submitted request is not completed merely by opening your email application or sending an email. The operator must verify the request, perform the applicable deletion and confirm the outcome.",
        "Specific manual-processing, log-retention and backup-purge schedules must be confirmed by the operator before this draft is published. Applicable rights-request deadlines still apply. Limited retention for a specific legal obligation or necessary claim must be explained, with its purpose and retention criteria.",
        "Save information you need before deleting your account. Removed key backups may make secure-message access unrecoverable. If a separately billed subscription is ever offered, cancel it with the billing provider as well.",
      ],
    },
    {
      id: "rights",
      heading: "Need help or want to challenge a decision?",
      paragraphs: [
        contact,
        "You retain applicable access, erasure and complaint rights. The Privacy Policy describes the rights-request process and regulator options.",
      ],
      links: [{ label: "Privacy rights", href: "/privacy#rights" }],
    },
  ],
};

export const legalDocs = { privacy, terms, cookies, deletion };
export type LegalDocKey = keyof typeof legalDocs;
export const legalLinks: {
  key: LegalDocKey;
  path: string;
  description: string;
}[] = [
  {
    key: "privacy",
    path: "/privacy",
    description: "Data, chat modes, recipients and your rights.",
  },
  {
    key: "terms",
    path: "/terms",
    description:
      "Accounts, acceptable use, calls and service responsibilities.",
  },
  {
    key: "cookies",
    path: "/cookies",
    description: "Website requests, storage and external services.",
  },
  {
    key: "deletion",
    path: "/delete-account",
    description: "Request deletion and understand what can remain.",
  },
];
